/**
 * 태그 추가 · 수정 팝업 (2026-09-05) — 정본 **B12 색 고르기** 의 첫 사용.
 *
 * ★★ **색판은 닫혀 있다.** 임의 hex 를 안 받는다 — 아무 색이나 받으면 어두운 바탕에서
 *    안 보이는 태그가 생기고, 그 대비는 아무도 안 잰다.
 * ★  고른 것은 **테두리 + 체크** 둘로 표시한다. 색만으로 가르면 색각 이상에서 안 보인다.
 * ★  고른 색으로 **실제 칩**을 그려 보인다 — 동그라미로만 고르면 글자가 얹혔을 때
 *    읽히는지를 모른다.
 */

import { useState } from 'react';
import { Check } from '../../icons';
import { Button } from '../../components/shared/Button';
import { FormField } from '../../components/shared/FormField';
import { Input } from '../../components/shared/Input';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import { MAIL_TAG_COLORS, type MailTag, type MailTagColor } from '../../api/mail';

export interface MailTagModalProps {
  /** 고치는 중이면 그 태그. 새로 만들면 null. */
  target?: MailTag | null;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (input: { name: string; color: MailTagColor }) => void;
}

const COLOR_LABEL: Record<MailTagColor, MailI18nKey> = {
  blue: 'mail.tag.c.blue', orange: 'mail.tag.c.orange', purple: 'mail.tag.c.purple',
  green: 'mail.tag.c.green', pink: 'mail.tag.c.pink', red: 'mail.tag.c.red', teal: 'mail.tag.c.teal',
};

export function MailTagModal({ target, busy, error, onClose, onSubmit }: MailTagModalProps) {
  const [name, setName] = useState(target?.name ?? '');
  const [color, setColor] = useState<MailTagColor>(target?.color ?? 'blue');
  const [local, setLocal] = useState<string | null>(null);

  const submit = () => {
    if (!name.trim()) { setLocal(tMail('mail.tag.err_name')); return; }
    setLocal(null);
    onSubmit({ name: name.trim(), color });
  };

  return (
    <Modal
      title={target ? tMail('mail.tag.edit_title') : tMail('mail.tag.add_title')}
      size="sm"
      onClose={onClose}
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={onClose} disabled={busy}>{tMail('mail.mbox.cancel')}</Button>
          <Button variant="primary" onClick={submit} loading={busy}>
            {target ? tMail('mail.tag.save') : tMail('mail.mbox.create')}
          </Button>
        </ModalActions>
      )}
    >
      <div className="mboxmodal">
        <FormField label={tMail('mail.tag.name_label')} required>
          <Input
            value={name}
            maxLength={40}
            autoFocus
            placeholder={tMail('mail.tag.name_ph')}
            onChange={(e) => { setName(e.target.value); if (local) setLocal(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (!busy) submit(); } }}
            fullWidth
          />
        </FormField>

        <FormField label={tMail('mail.tag.color_label')}>
          <div className="tagpick" role="radiogroup" aria-label={tMail('mail.tag.color_label')}>
            {MAIL_TAG_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={color === c}
                aria-label={tMail(COLOR_LABEL[c])}
                title={tMail(COLOR_LABEL[c])}
                className={`tagpick__dot${color === c ? ' is-on' : ''}`}
                data-tagcolor={c}
                onClick={() => setColor(c)}
              >
                <Check size={13} aria-hidden />
              </button>
            ))}
          </div>
        </FormField>

        <div className="tagpick__preview">
          <span className="tagpick__previewlabel">{tMail('mail.tag.preview')}</span>
          <span className="mailtag" data-tagcolor={color}>{name.trim() || tMail('mail.tag.name_ph')}</span>
        </div>

        {(local ?? error) && <p className="mboxmodal__err" role="alert">{local ?? error}</p>}
      </div>
    </Modal>
  );
}
